import * as React from "react"
import Subtitle from "../../../atoms/typography/Subtitle"
import { HorizontalSpacer, Spacer } from "../../../atoms/spacers"
import { Col, Row, Visible } from "react-grid-system"
import CustomCheckbox from "../../../atoms/inputs/CustomCheckbox"
import H3 from "../../../atoms/typography/H3"
import { User } from "../../../interfaces/SaleorTypes"
import SmallColorfulButton from "../../../atoms/buttons/SmallColorfulButton"
import TextInput from "../../TextInput"
import Select from "../../Select"
import AddressValidationRules from "../../../atoms/inputs/AddressValidationRules"
import { useAcceptedCountries } from "../../../../hooks/useAcceptedCountries"
import LoadingSpinner from "../../../atoms/LoadingSpinner"
import { ShippingInfo } from "../../../pages/products/BookRental"
import { captureExceptionWithContext } from "../../../../utils/sentry"

interface Props {
  onClickProceedToPayment: (formData?: ShippingInfo) => void
  shippingSameAsBilling: boolean
  onPressSameAsBilling: () => void
  user?: User
  fieldsDisabled?: boolean
  hasProceededToPayment: boolean
}

const RentalShippingForm: React.FC<Props> = ({
  onClickProceedToPayment,
  shippingSameAsBilling,
  onPressSameAsBilling,
  user,
  fieldsDisabled,
  hasProceededToPayment,
}) => {
  const { countries, error, loading } = useAcceptedCountries()

  const [formData, setFormData] = React.useState<ShippingInfo>({
    sFirstName: user?.firstName ?? "",
    sLastName: user?.lastName ?? "",
    sStreetAdress1: user?.shippingAddress?.address1 ?? "",
    sStreetAdress2: user?.shippingAddress?.address2 ?? "",
    sCity: user?.shippingAddress?.city ?? "",
    sPostalCode: user?.shippingAddress.zipCode ?? "",
    sCountry: user?.shippingAddress?.country ?? "",
    sCountryArea: "",
    sPhone: user?.mobilePhoneNumber ?? "",
  })

  const handleChangeShippingAddress = (e: any) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  if (error) {
    captureExceptionWithContext(error, "Error fetching countries for address field")
  }
  if (loading) {
    return <LoadingSpinner />
  }

  const onClickPaymnet = () => {
    onClickProceedToPayment(formData)
  }

  return (
    <div>
      <Subtitle>Shipping</Subtitle>
      <HorizontalSpacer spacing={2} />
      <Row align="center" nogutter>
        <CustomCheckbox checked={shippingSameAsBilling} onChange={onPressSameAsBilling} type="checkbox" />
        <Spacer spacing={0.5} />
        <H3>Same as billing</H3>
      </Row>

      {!shippingSameAsBilling && (
        <div>
          <HorizontalSpacer spacing={2} />
          <div>
            <Row justify="between" align="center">
              <Col md={6} xs={12}>
                <TextInput
                  textFieldTitle="Recipients First name"
                  type="text"
                  name="sFirstName"
                  id="sFirstName"
                  onChange={handleChangeShippingAddress}
                  defaultValue={formData.sFirstName}
                  required
                  disabled={fieldsDisabled}
                />
              </Col>
              <Col md={6} xs={12}>
                <Visible xs>
                  <HorizontalSpacer spacing={4} />
                </Visible>
                <TextInput
                  textFieldTitle="Recipients Last name"
                  defaultValue={formData.sLastName}
                  type="text"
                  name="sLastName"
                  id="sLastName"
                  onChange={handleChangeShippingAddress}
                  required
                  disabled={fieldsDisabled}
                />
              </Col>
            </Row>
            <HorizontalSpacer spacing={2} />
            <Row justify="between" align="center">
              <Col md={6} xs={12}>
                <Visible xs>
                  <HorizontalSpacer spacing={4} />
                </Visible>
                <TextInput
                  textFieldTitle="Recipients Mobile Phone"
                  disabled={fieldsDisabled}
                  defaultValue={formData.sPhone}
                  type="tel"
                  name="sPhone"
                  id="sPhone"
                  onChange={handleChangeShippingAddress}
                  required
                />
              </Col>
            </Row>
            <HorizontalSpacer spacing={2} />

            <Row justify="between" align="center">
              <Col md={6} xs={12}>
                <TextInput
                  textFieldTitle="Shipping address 1"
                  defaultValue={formData.sStreetAdress1}
                  disabled={fieldsDisabled}
                  type="text"
                  name="sStreetAdress1"
                  id="sStreetAdress1"
                  onChange={handleChangeShippingAddress}
                  required
                />
              </Col>
              <Col md={6} xs={12}>
                <Visible xs>
                  <HorizontalSpacer spacing={4} />
                </Visible>
                <TextInput
                  textFieldTitle="Shipping address 2 (optional)"
                  defaultValue={formData.sStreetAdress2}
                  disabled={fieldsDisabled}
                  type="text"
                  name="sStreetAdress2"
                  id="sStreetAdress2"
                  onChange={handleChangeShippingAddress}
                />
              </Col>
            </Row>
            <HorizontalSpacer spacing={2} />

            <Row justify="between" align="center">
              <Col md={6} xs={12}>
                <TextInput
                  textFieldTitle="City"
                  defaultValue={formData.sCity}
                  disabled={fieldsDisabled}
                  type="text"
                  name="sCity"
                  id="sCity"
                  onChange={handleChangeShippingAddress}
                  required
                />
              </Col>
              <Col md={6} xs={12}>
                <Visible xs>
                  <HorizontalSpacer spacing={4} />
                </Visible>
                <TextInput
                  textFieldTitle="Postal code"
                  defaultValue={formData.sPostalCode}
                  disabled={fieldsDisabled}
                  type="text"
                  name="sPostalCode"
                  id="sPostalCode"
                  onChange={handleChangeShippingAddress}
                  required
                />
              </Col>
            </Row>
            <HorizontalSpacer spacing={2} />

            <Row justify="between" align="center">
              <Col md={6} xs={12}>
                <Select
                  title="Country"
                  disabled={fieldsDisabled}
                  defaultValue={formData.sCountry}
                  options={countries ?? []}
                  id="sCountry"
                  name="sCountry"
                  onChange={handleChangeShippingAddress}
                  required
                />
              </Col>
              {formData.sCountry?.length > 0 && (
                <Col md={6} xs={12}>
                  <AddressValidationRules
                    countryCode={formData.sCountry}
                    name="sCountryArea"
                    id="sCountryArea"
                    onChange={handleChangeShippingAddress}
                  />
                </Col>
              )}
            </Row>
          </div>
        </div>
      )}

      {!hasProceededToPayment && (
        <>
          <HorizontalSpacer spacing={2} />
          <Row justify="center">
            <SmallColorfulButton variant="yellow" title="Proceed to payment" onClick={onClickPaymnet} />
          </Row>
          <HorizontalSpacer spacing={2} />
        </>
      )}
    </div>
  )
}

export default RentalShippingForm
